import { AuthContext, useIsHydrated } from '@core/shared';
import { OptionsApi } from '@core/swagger-client';
import { Logo, ProfileAbbreviation, ProfileName } from '@core/ui';
import { Link, useLocation } from '@online-webinar/router';
import { useMutation } from '@tanstack/react-query';
import { debounce } from 'lodash-es';
import { ElementType, useContext, useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDismissableMenu } from '../../hooks/useDismissableMenu';
import { SideNavBarProps } from './types';

export const SideNavBar = ({ navBarMenuItems }: SideNavBarProps) => {
    const location = useLocation();
    const { t } = useTranslation();

    const isHydrated = useIsHydrated();

    const { isVerified, isAuthenticated, isMainAccount, mainAccount, hasRights, managedAccounts, updateAuthState } = useContext(AuthContext)!;
    const profileBorderColor = isAuthenticated && isVerified ? 'border-account bg-account' : 'border-gray-500 bg-gray-500';

    // The profile menu is a popover: the browser hides it with display: none (out of the tab order), closes it on
    // Escape or a click outside, and puts its content right after the button in the tab order
    const profileMenuId = useId();
    const profileMenuRef = useRef<HTMLDivElement>(null);
    const [profileMenuOpen, setProfileMenuOpen] = useState(false);
    const profileBtnActiveClass = profileMenuOpen ? 'border-primary bg-primary text-on-primary' : `${profileBorderColor} text-white`;

    const [open, setMenu] = useState(false);
    const [buttonClicked, setButtonClicked] = useState(false);
    const menuBreakpoint = useMemo(() => {
        let fontSize = 16;
        if (globalThis.document && globalThis.getComputedStyle) {
            fontSize = parseFloat(globalThis.getComputedStyle(globalThis.document.documentElement).fontSize);
        }
        return 40 * fontSize;
    }, []);

    const [scrollDirection, setScrollDirection] = useState('none');
    const [prevScrollY, setPrevScrollY] = useState(0);
    const [windowWidth, setWindowWidth] = useState(globalThis.window?.innerWidth || 0);

    useLayoutEffect(() => {
        if (!globalThis.window) {
            return;
        }

        const handleScroll = debounce(() => {
            const currentScrollY = globalThis.window.scrollY;

            if (windowWidth < menuBreakpoint) {
                if (currentScrollY > prevScrollY) {
                    setScrollDirection('down');
                } else if (currentScrollY < prevScrollY) {
                    setScrollDirection('up');
                }
                setPrevScrollY(currentScrollY);
            } else {
                // Reset scroll direction if the window width is >= menuBreakpoint
                setScrollDirection('none');
                setPrevScrollY(0);
            }
        }, 25);

        globalThis.window.addEventListener('scroll', handleScroll);

        // Call handleScroll initially to set the correct state
        handleScroll();

        // Clean up the event listener on component unmount
        return () => {
            globalThis.window.removeEventListener('scroll', handleScroll);
        };
    }, [prevScrollY, windowWidth]);

    useLayoutEffect(() => {
        if (!globalThis.window) {
            return;
        }

        const handleResize = debounce(() => {
            const newWindowWidth = globalThis.window.innerWidth;
            setWindowWidth(newWindowWidth);

            // 40rem is the breakpoint where the mobile menu is toggled
            if (newWindowWidth >= menuBreakpoint) {
                setMenu(true);
            } else {
                setMenu(false);
            }
        }, 50);

        globalThis.window.addEventListener('resize', handleResize);

        // Call handleResize initially to set the correct state
        handleResize();

        // Clean up the event listener on component unmount
        return () => {
            globalThis.window.removeEventListener('resize', handleResize);
        };
    }, []);

    useEffect(() => {
        setMenu(false);
    }, [location]);

    const handleClick = () => {
        setButtonClicked(true);
        setMenu(!open);
    };

    // Below the menu breakpoint the panel slides over the page: close it when focus leaves it, or on Escape
    const menuToggleRef = useRef<HTMLButtonElement>(null);
    const dismissMenu = useDismissableMenu(open && windowWidth < menuBreakpoint, () => setMenu(false), menuToggleRef);

    const { mutate: logout } = useMutation(
        OptionsApi.login.logout({
            onSuccess: () => {
                updateAuthState({ isAuthenticated: false, token: null, mainAccount: null, activeAccount: null, managedAccounts: [], rights: [] });
            }
        })
    );

    return (
        <div
            id="main-nav"
            className="menu:fixed w-full menu:w-20 menu:h-full bg-white flex menu:flex-col items-center justify-between menu:justify-start px-4 py-3 menu:px-3 menu:py-4 z-20"
        >
            {isAuthenticated && (
                <div
                    onClick={handleClick}
                    className={`${open ? 'opacity-60 translate-y-0' : 'opacity-0 translate-y-full'} block menu:hidden fixed top-0 left-0 w-full h-full bg-black transition-opacity ease-out-quint duration-300 motion-reduce:duration-0 z-30`}
                ></div>
            )}

            <Link className="relative w-14 h-14 menu:mb-6" to="/">
                <Logo />
                <span className="sr-only">{t('Generic.Link.Logo', { site: 'OnlineWebinar', page: t('Schedule.PageHeading') })}</span>
            </Link>

            {isAuthenticated && mainAccount && (
                <nav
                    className={`fixed menu:relative bottom-0 left-0 w-full bg-white flex justify-between menu:flex-col menu:flex-1 px-4 py-3 menu:p-0 shadow-menu menu:shadow-none ${scrollDirection !== 'down' ? 'translate-y-0' : 'translate-y-full'} transition-transform ease-out-quint duration-300 menu:duration-0 motion-reduce:duration-0 z-50`}
                >
                    {/* Only the toggle and the panel: tabbing on to the profile button must close the menu */}
                    <div className="contents" {...dismissMenu}>
                        <button
                            ref={menuToggleRef}
                            className="relative w-12 h-12 menu:hidden bg-white rounded-xl z-10"
                            onClick={handleClick}
                            aria-expanded={open}
                            aria-label={`${open ? t('MainMenu.Toggle.Close') : t('MainMenu.Toggle.Open')}`}
                        >
                            <span
                                className={`absolute left-2 w-8 h-0.5 bg-black block rounded-xs motion-reduce:[animation-duration:0s] translate-y-[calc(-50%-.375rem)] ${buttonClicked && (open ? 'animate-menuToggleToCloseTop' : 'animate-menuToggleToOpenTop')}`}
                            ></span>
                            <span
                                className={`absolute left-2 w-8 h-0.5 bg-black block rounded-xs motion-reduce:[animation-duration:0s] ${buttonClicked && (open ? 'animate-menuToggleToCloseCenter' : 'animate-menuToggleToOpenCenter')}`}
                            ></span>
                            <span
                                className={`absolute left-2 w-8 h-0.5 bg-black block rounded-xs motion-reduce:[animation-duration:0s] translate-y-[calc(50%+.375rem)] ${buttonClicked && (open ? 'animate-menuToggleToCloseBottom' : 'animate-menuToggleToOpenBottom')}`}
                            ></span>
                        </button>
                        <div
                            className={`${open ? 'flex translate-y-0 starting:translate-y-full' : 'hidden translate-y-full'} menu:flex menu:translate-y-0 absolute bottom-0 left-0 menu:relative w-full bg-white flex-col justify-end px-4 pt-12 pb-20 menu:p-0 transition-[translate,display] transition-discrete ease-out-quint duration-500 menu:duration-0 motion-reduce:duration-0`}
                        >
                            {navBarMenuItems.map((menuItem: { menuItemComponent: ElementType; name: string }) => {
                                const Component = menuItem.menuItemComponent;

                                return <Component key={menuItem.name} />;
                            })}
                        </div>
                    </div>

                    <div className="menu:pb-3">
                        <button
                            data-testid="profile-menu-button"
                            disabled={!isHydrated}
                            popoverTarget={profileMenuId}
                            aria-expanded={profileMenuOpen}
                            className={`size-12 menu:size-14 border-2 border-solid ${profileBtnActiveClass} hover:border-primary rounded-xl text-center text-xl uppercase transition-colors ease-in-out duration-200`}
                        >
                            {/* The initials are an avatar, not a label: screen readers get "My account" instead */}
                            <span aria-hidden="true">
                                <ProfileAbbreviation profile={mainAccount.profile} />
                            </span>
                            <span className="sr-only">{t('Account.Button')}</span>
                        </button>
                        {/* In the top layer, so positioned against the viewport: above the mobile bottom bar (4.5rem), or
                            next to the desktop sidebar (5rem wide); both are fixed, as the old position was */}
                        <div
                            id={profileMenuId}
                            ref={profileMenuRef}
                            popover="auto"
                            onToggle={(event) => setProfileMenuOpen(event.newState === 'open')}
                            // Choosing an item closes the menu; in-app navigation keeps this sidebar mounted
                            onClick={(event) => {
                                if ((event.target as HTMLElement).closest('a, button')) profileMenuRef.current?.hidePopover?.();
                            }}
                            className="inset-auto bottom-18 right-0 menu:right-auto menu:bottom-4 menu:left-20 m-0 p-0 border-0 max-w-full menu:max-w-md min-w-72 bg-white text-center rounded-l-lg menu:rounded-l-none menu:rounded-r-lg overflow-hidden shadow-3xl origin-bottom-right menu:origin-bottom-left opacity-0 scale-75 open:opacity-100 open:scale-100 starting:open:opacity-0 starting:open:scale-75 transition-[opacity,scale,display,overlay] transition-discrete duration-300 motion-reduce:duration-0"
                        >
                            <div className="p-6">
                                <div className="flex flex-col mb-3">
                                    <span className="text-sm">{t('Account.Menu.Welcome')}</span>
                                    <span className="text-primary font-medium text-3xl font-accent leading-none wrap-break-word mb-4">
                                        <ProfileName profile={mainAccount.profile} />
                                    </span>
                                    {managedAccounts?.length > 0 && (
                                        <Link to="/switch-account" className="py-3 font-w-button text-gray-500 hover:text-black -mt-2">
                                            {t('Account.Menu.SwitchAccount')}
                                        </Link>
                                    )}
                                    <div className="hor-divider h-px text-primary mt-2"></div>
                                </div>
                                <div className="text-gray-500 flex flex-col whitespace-nowrap">
                                    {!isMainAccount && (
                                        <>
                                            <span className="text-sm text-left whitespace-normal">{t('Account.Menu.NotOnMainAccount')}</span>
                                        </>
                                    )}
                                    {isMainAccount && (
                                        <>
                                            <Link to="/account/$tab" params={{ tab: 'profile' }} className="py-3 font-w-button hover:text-black text-left">
                                                {t('Account.Menu.Profile')}
                                            </Link>
                                            <Link to="/account/$tab" params={{ tab: 'password' }} className="py-3 font-w-button hover:text-black text-left">
                                                {t('Account.Menu.Password')}
                                            </Link>
                                            {hasRights && (
                                                <Link to="/account/$tab" params={{ tab: 'manage' }} className="py-3 font-w-button hover:text-black text-left">
                                                    {t('Account.Menu.ManageAccount')}
                                                </Link>
                                            )}
                                        </>
                                    )}
                                    <button data-testid="logout-button" onClick={() => logout()} className="py-3 font-w-button hover:text-black text-left">
                                        {t('Account.Menu.Logout')}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </nav>
            )}
        </div>
    );
};
